/**
 * AppShell Component
 * Main application layout wrapper with sidebar and top navigation
 * Includes authentication check for protected pages
 */
'use client';

import React, { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Sidebar, TopBar } from '@/components/navigation';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';

interface AppShellProps {
  children: React.ReactNode;
}

// Pages that should NOT show the navigation (public pages)
const publicPages = ['/login', '/register', '/forgot-password', '/reset-password', '/'];

// Pages that need more width - sidebar auto-collapses on these
// Note: Only visual editors need this, NOT the campaign wizard
const wideContentPages: string[] = [];

// Check if this is a visual editor page that needs wide content
const isVisualEditorPage = (path: string | null): boolean => {
  if (!path) return false;
  // Landing page editor: /landing-pages/[id]/edit
  if (path.match(/^\/landing-pages\/[^/]+\/edit/)) return true;
  // Email template editor: /emails/[id]/edit
  if (path.match(/^\/emails\/[^/]+\/edit/)) return true;
  // Workflow editor: /workflows/[id]/edit
  if (path.match(/^\/workflows\/[^/]+\/edit/)) return true;
  return false;
};

export function AppShell({ children }: AppShellProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [autoCollapsed, setAutoCollapsed] = useState(false);

  // Check if this is a public page
  const isPublicPage = publicPages.some((page) => pathname === page || (page !== '/' && pathname?.startsWith(page)));

  // Check if this page needs wide content (auto-collapse sidebar)
  const needsWideContent = isVisualEditorPage(pathname);

  // Check authentication on mount and when pathname changes
  useEffect(() => {
    setMounted(true);

    // Auto-collapse sidebar on wide content pages
    if (needsWideContent) {
      setSidebarCollapsed(true);
      setAutoCollapsed(true);
    } else if (autoCollapsed) {
      // Restore user's preference when leaving wide content pages
      const saved = localStorage.getItem('sidebar_collapsed');
      setSidebarCollapsed(saved === 'true');
      setAutoCollapsed(false);
    } else {
      // Normal page - use saved preference
      const saved = localStorage.getItem('sidebar_collapsed');
      if (saved !== null) {
        setSidebarCollapsed(saved === 'true');
      }
    }

    // Check if user has a token
    const token = localStorage.getItem('access_token');
    if (!token && !isPublicPage) {
      // No token and not on public page - redirect to login
      setIsAuthenticated(false);
      router.push('/login');
    } else if (token) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(true); // Public page, no auth needed
    }
  }, [pathname, isPublicPage, router, needsWideContent]);

  // Save sidebar state to localStorage
  const handleToggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebar_collapsed', String(newState));
  };

  // Don't render navigation on public pages
  if (isPublicPage) {
    return <>{children}</>;
  }

  // Show loading while checking auth
  if (!mounted || isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-brand-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-orange" />
      </div>
    );
  }

  // Redirect happening, show nothing
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-brand-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-orange mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark text-gray-900 dark:text-white relative overflow-hidden selection:bg-brand-orange selection:text-white transition-colors duration-300">

      {/* Technical Grid Background - Dark Mode Only */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0 opacity-0 dark:opacity-100 transition-opacity duration-500">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-dark via-brand-dark-secondary to-brand-dark" />

        {/* Very subtle ambient glow - Purple (bottom left) */}
        <div
          className="absolute -bottom-1/4 -left-1/4 w-[60%] h-[50%] rounded-full opacity-[0.08]"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.5) 0%, transparent 60%)',
            filter: 'blur(80px)',
          }}
        />

        {/* Very subtle ambient glow - Orange (top right) */}
        <div
          className="absolute -top-1/4 -right-1/4 w-[50%] h-[40%] rounded-full opacity-[0.06]"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(255, 107, 0, 0.4) 0%, transparent 60%)',
            filter: 'blur(60px)',
          }}
        />

        {/* Technical Blueprint Grid */}
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* Fine grid (20px) */}
            <pattern id="fine-grid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(139, 92, 246, 0.08)" strokeWidth="0.5" />
            </pattern>
            {/* Major grid (100px) with crosshairs at intersections */}
            <pattern id="major-grid" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              {/* Major grid lines */}
              <path d="M 100 0 L 0 0 0 100" fill="none" stroke="rgba(139, 92, 246, 0.15)" strokeWidth="0.5" />
              {/* Crosshair marks at intersection */}
              <line x1="0" y1="-3" x2="0" y2="3" stroke="rgba(139, 92, 246, 0.25)" strokeWidth="0.5" />
              <line x1="-3" y1="0" x2="3" y2="0" stroke="rgba(139, 92, 246, 0.25)" strokeWidth="0.5" />
            </pattern>
          </defs>
          {/* Render fine grid first */}
          <rect width="100%" height="100%" fill="url(#fine-grid)" />
          {/* Render major grid on top */}
          <rect width="100%" height="100%" fill="url(#major-grid)" />
        </svg>

        {/* Subtle vignette effect */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, transparent 50%, rgba(3, 7, 18, 0.4) 100%)',
          }}
        />
      </div>

      {/* Sidebar Navigation */}
      <Sidebar collapsed={sidebarCollapsed} onToggle={handleToggleSidebar} />

      {/* Top Bar */}
      <TopBar sidebarCollapsed={sidebarCollapsed} onToggleSidebar={handleToggleSidebar} />

      {/* Main Content */}
      <main
        className={`pt-20 min-h-screen transition-all duration-300 relative z-10 ${
          sidebarCollapsed ? 'pl-16' : 'pl-64'
        }`}
      >
        <ImpersonationBanner />
        {children}
      </main>
    </div>
  );
}

export default AppShell;
